<template>
  <div class="min-h-full" :class="{'bg-gray-200': failed}">
    <div class="pt-8" v-if="loading">
      <loading />
    </div>
    <div class="p-6 stack-sm" v-else>
      <h2 class="mb-0 leading-tight inline cursor-pointer" @click="openActivity">
        <span v-if="finished">{{ target.target }} Mal</span>
        {{ activity.title }}
        <icon name="Link" />
      </h2>
      <div class="leading-tight">
        <router-link :to="'/profile/' + user.id">
          <p
            class="tracking-wide font-medium"
          >{{ $t('targets.target_created_by') + ": " + user.username }}</p>
        </router-link>
        <p
          class="italic"
          v-if="unstarted"
        >{{ capitalize($t('targets.starts')) }} {{ timeUntil(target.end) }}</p>
        <p
          class="italic"
          v-else-if="!finished"
        >{{ capitalize($t('general.until')) }} {{ target.end | formatDateNormal }}</p>
      </div>
      <div class="text-sm grid items-center justify-center border-t border-b mt-8 mb-4 py-4">
        <div class="text-green-500 text-center" v-if="finished">
          <icon name="Check" size="5em" :settings="{ strokeWidth: 1.2 }" />
        </div>
        <p
          class="text-4xl mr-2 text-center leading-none"
          :class="{ 'text-green-500': finished, 'text-red-500': failed }"
          v-if="!finished"
        >
          <span v-if="unstarted">{{ $tc('targets.target', 1) }}: {{ target.target }}</span>
          <span v-else>{{ target.value }}/{{ target.target }}</span>
        </p>
        <p class="text-center">
          <span class="text-lg text-red-500" v-if="failed">{{ $t("targets.target_failed") }}</span>
          <span v-else-if="!unstarted">{{ $t("general.finished") }}</span>
        </p>
        <div class="mt-2" v-if="failed">
          <wd-button black icon="Retry" @input="openRetryTarget">{{ $t("targets.retry") }}</wd-button>
        </div>
        <div class="mt-2" v-else-if="!finished && !unstarted && !checkedToday">
          <wd-button
            class="text-base"
            feature
            icon="Check"
            @input="finishActivity"
          >{{ $t("targets.finish_activity") }}</wd-button>
        </div>
      </div>

      <!-- Group members -->
      <div class="border border-gray-300 p-2 mb-4" v-if="target.group">
        <friend v-for="friend in friends" :friend="friend" :key="friend.id" />
        <friend
          v-for="friend in targetInvitationFriends"
          :friend="friend"
          :key="friend.id"
          pending
        />
        <div class="my-2">
          <wd-button black transparent @input="openAddFriends">
            {{
            $t("targets.invitation.invite_friend")
            }}
          </wd-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Target } from "@/modules/targets/viewmodels/Target";
import { Activity } from "@/modules/activities/viewmodels/Activity";

import FormMixin from "@/mixins/FormMixin";
import DateMixin from "@/mixins/DateMixin";
import moment from "moment";

const Friend = require("@/modules/users/components/Friend.vue").default;

export default {
  name: "TargetDetailSite",
  mixins: [DateMixin, FormMixin],
  data() {
    return {
      target: null,
      targetInvitations: null,
      targetInvitationFriends: [],
      activity: null,
      user: null,
      friends: []
    };
  },
  props: {
    id: {
      type: String,
      default: null
    }
  },
  components: {
    Friend
  },
  methods: {
    async finishActivity() {
      if (this.sending) return;
      this.sending = true;
      await this.$store.getters["activityentry/api"].Custom.Check(
        this.activity.id
      );
      this.popup({
        type: "success",
        title: this.$t("activities.activity_finished"),
        text: this.$t("activities.activity_finished_message", {
          activity: this.activity.title
        }),
        actions: [
          {
            title: this.$t("general.undo"),
            location: "right",
            action: () => {
              this.unFinishActivity();
            }
          }
        ],
        autohide: true
      });
      this.target.value++;
      this.sending = false;
    },
    async unFinishActivity() {
      if (this.sending) return;
      this.sending = true;
      await this.$store.getters["activityentry/api"].Custom.Uncheck(
        this.activity.id
      );
      this.popup({
        type: "info",
        title: this.$t("general.reverted_changes"),
        autohide: true
      });
      this.sending = false;
      this.target.value--;
    },
    openActivity() {
      this.goTo("/activity/" + this.activity.id);
    },
    openAddFriends() {
      this.goTo("/target-invite/" + this.id);
    },
    openRetryTarget() {
      this.goTo("/target-retry/" + this.id);
    },
    async deleteTargetMember() {
      let targetMember = this.$store.getters["targetmember/targetTargetMember"](
        this.target.id
      );
      await this.$store.getters["targetmember/api"].Destroy(targetMember.id);
      await this.$store.dispatch("target/removeTarget", targetMember.target_id);
      this.popup({
        type: "success",
        title: this.$t("general.requests.delete_successful"),
        text: this.$t("targets.you_no_longer_in_target")
      });

      this.goTo("/targets");
    },
    editTarget() {
      this.goTo("/update-target/" + this.id);
    }
  },
  computed: {
    areaWord() {
      return this.area === "today"
        ? this.$t("general.today")
        : this.$t("general.this_week");
    },
    lastEntry() {
      return this.activity
        ? this.$store.getters["activityentry/lastEntryForActivity"](
            this.activity.id
          )
        : false;
    },
    checkedToday() {
      return this.lastEntry ? this.isToday(this.lastEntry) : false;
    },
    finished() {
      return this.target ? this.target.status === "finished" : false;
    },
    failed() {
      return this.target ? this.target.status === "failed" : false;
    },
    unstarted() {
      return this.target ? this.target.status === "unstarted" : false;
    }
  },
  async created() {
    this.target = await this.$store.getters["target/api"].Show(this.id);
    this.target.target_members.forEach(async e => {
      let user = await this.$store.getters["users/api"].Show(e.user_id);
      this.friends.push(user);
    });
    await this.$store.getters["target/api"].Custom.My();
    await this.$store.getters["targetmember/api"].Custom.My();
    this.activity = await this.$store.getters["activity/api"].Show(
      this.target.activity_id
    );
    this.user = await this.$store.getters["users/api"].Show(
      this.target.user_id
    );
    await this.$store.getters["activityentry/api"].Custom.ActivityLast(
      this.activity.id
    );

    this.targetInvitations = await this.$store.getters[
      "target/api"
    ].Custom.Invitations(this.target.id);
    await this.targetInvitations.forEach(async e => {
      let friend = await this.$store.getters["users/api"].Show(e.user_id);
      this.targetInvitationFriends.push(friend);
    });

    let elements = [
      {
        value: this.$t("general.delete"),
        action: () => {
          this.deleteTargetMember();
        }
      }
    ];
    if (this.target.user_id === this.$store.getters["auth/ownId"]) {
      elements.unshift({
        value: this.$t("general.edit"),
        action: () => {
          this.editTarget();
        }
      });
    }
    let headerSettings = {
      elements: [
        {
          value: this.$t("targets.target_details")
        }
      ],
      backbutton: true,
      clear: true
    };
    if (!this.finished) {
      headerSettings.elements.push({
        type: "dropdown",
        location: "right",
        elements: elements
      });
    }

    this.$store.dispatch("header/update", headerSettings);
    this.loaded();
  }
};
</script>
